import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { ToastService } from '../../../services/toast/toast.service';
import { LoaderComponent } from '../../../atoms/loader/loader.component';
import { CENTRAL_PLACE_ID, UserAccessApi } from '../../../common-files/user-access-api';
import { AssignRoleDialogComponent } from '../assign-role-dialog/assign-role-dialog.component';
import { AddPermissionDialogComponent } from '../add-permission-dialog/add-permission-dialog.component';

// a user's access, grouped Place -> Role -> View -> Permission (the same grouping
// the sign-in response uses; the central place has establishmentId null). Roles
// expand to show their permissions. Admins can add roles / permissions ("Add" menu)
// and switch a role, a whole view, or a single permission on and off.
// The backend comes in through data.api (establishment staff or super admin).
// Closes with `true` when anything changed so the list refreshes.
@Component({
  selector: 'app-manage-user-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, MatMenuModule, LoaderComponent],
  templateUrl: './manage-user-dialog.component.html',
  styleUrls: ['./manage-user-dialog.component.scss', '../../../../common-style/ac-dialog.scss'],
})
export class ManageUserDialogComponent implements OnInit {
  access: any = null;
  isLoading: boolean = false;
  canManageUsers: boolean = false;
  roleFilter = new FormControl<string>(''); // roleKey, '' = all roles
  permissionSearch = new FormControl('');
  private expanded = new Set<number>();
  private changed: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<ManageUserDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { sumId: number; name: string; api: UserAccessApi },
    private toastService: ToastService,
    private dialog: MatDialog
  ) {}

  get api(): UserAccessApi {
    return this.data.api;
  }

  ngOnInit(): void {
    this.canManageUsers = this.api.canManage;
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.api.getAccess(this.data.sumId).subscribe({
      next: (res: any) => {
        this.access = res?.data || null;
        // open the roles straight away when there are only a few
        const roles = this.allRoles();
        if (roles.length <= 2) roles.forEach((r) => this.expanded.add(r.suremId));
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load user access.');
      },
    });
  }

  private allRoles(): any[] {
    return (this.access?.establishments || []).flatMap((e: any) => e.roles || []);
  }

  // stable key for a place (the central place has establishmentId null). A method,
  // not `??` in the template: Angular 18 miscompiles `??` inside `track`.
  placeKey(place: any): string {
    return place?.establishmentId == null ? 'central' : String(place.establishmentId);
  }

  // the distinct roles the user holds, for the Role dropdown
  roleOptions(): { roleKey: string; roleName: string }[] {
    const options: { roleKey: string; roleName: string }[] = [];
    for (const r of this.allRoles()) {
      if (!options.some((o) => o.roleKey === r.roleKey)) options.push({ roleKey: r.roleKey, roleName: r.roleName });
    }
    return options;
  }

  // establishments with the roles matching the Role dropdown (empty ones drop out)
  filteredEstablishments(): any[] {
    const roleKey = this.roleFilter.value || '';
    return (this.access?.establishments || [])
      .map((e: any) => ({
        ...e,
        roles: (e.roles || []).filter((r: any) => !roleKey || r.roleKey === roleKey),
      }))
      .filter((e: any) => e.roles.length > 0);
  }

  // views of a role whose label or permissions match the permission search
  filteredViews(role: any): any[] {
    const term = (this.permissionSearch.value || '').trim().toLowerCase();
    if (!term) return role.views || [];
    return (role.views || [])
      .map((v: any) => {
        const viewMatches = String(v.viewLabel || v.viewName).toLowerCase().includes(term);
        const permissions = viewMatches
          ? v.permissions
          : (v.permissions || []).filter((p: any) =>
              String(p.permLabel || p.permission).toLowerCase().includes(term)
            );
        return { ...v, permissions };
      })
      .filter((v: any) => v.permissions.length > 0);
  }

  permissionCount(role: any): number {
    return (role.views || []).reduce(
      (n: number, v: any) => n + (v.permissions || []).filter((p: any) => p.isActive).length,
      0
    );
  }

  isExpanded(suremId: number): boolean {
    return this.expanded.has(suremId);
  }

  toggleExpanded(suremId: number): void {
    if (this.expanded.has(suremId)) this.expanded.delete(suremId);
    else this.expanded.add(suremId);
  }

  canAct(role?: any): boolean {
    if (!this.canManageUsers || !this.access?.canManage) return false;
    return role ? !!role.canManage : true;
  }

  addRole(): void {
    if (!this.canAct()) return;
    const ref = this.dialog.open(AssignRoleDialogComponent, {
      width: '560px',
      maxWidth: 'calc(100vw - 32px)',
      data: { sumId: this.data.sumId, name: this.data.name, api: this.api },
    });
    ref.afterClosed().subscribe((changed: boolean) => {
      if (changed) this.markChanged();
    });
  }

  toggleRole(role: any): void {
    if (!this.canAct(role)) return;
    this.isLoading = true;
    this.api.setRoleStatus(this.data.sumId, role.suremId, !role.isActive).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Role updated.');
        this.markChanged();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to update the role.');
      },
    });
  }

  // Establishment -> Role -> View -> Permission picker; only manageable, active roles
  addPermission(): void {
    if (!this.canAct()) return;
    const establishments = (this.access?.establishments || [])
      .map((e: any) => ({
        // the central place has no id; the picker uses 0 for it
        establishmentId: e.establishmentId == null ? CENTRAL_PLACE_ID : e.establishmentId,
        name: e.name,
        roles: (e.roles || []).filter((r: any) => r.canManage && r.isActive),
      }))
      .filter((e: any) => e.roles.length > 0);
    if (!establishments.length) {
      this.toastService.error('Add an active role first, then add permissions to it.');
      return;
    }
    const ref = this.dialog.open(AddPermissionDialogComponent, {
      width: '720px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100dvh - 32px)',
      data: { sumId: this.data.sumId, name: this.data.name, establishments, api: this.api },
    });
    ref.afterClosed().subscribe((changed: boolean) => {
      if (changed) this.markChanged();
    });
  }

  // a view is "on" while any of its permissions is active
  isViewOn(view: any): boolean {
    return (view.permissions || []).some((p: any) => p.isActive);
  }

  // switch every permission of the view together
  toggleView(role: any, view: any): void {
    if (!this.canAct(role)) return;
    const supdIds = (view.permissions || []).map((p: any) => p.supdId);
    if (!supdIds.length) return;
    this.isLoading = true;
    this.api.setPermissionsStatus(this.data.sumId, supdIds, !this.isViewOn(view)).subscribe({
        next: (res: any) => {
          this.toastService.success(res?.message || 'View updated.');
          this.markChanged();
        },
        error: (err) => {
          this.isLoading = false;
          this.toastService.error(err?.error?.message || 'Failed to update the view.');
        },
      });
  }

  togglePermission(role: any, permission: any): void {
    if (!this.canAct(role)) return;
    this.isLoading = true;
    this.api.setPermissionStatus(this.data.sumId, permission.supdId, !permission.isActive).subscribe({
        next: (res: any) => {
          this.toastService.success(res?.message || 'Permission updated.');
          this.markChanged();
        },
        error: (err) => {
          this.isLoading = false;
          this.toastService.error(err?.error?.message || 'Failed to update the permission.');
        },
      });
  }

  // the establishment-side backend offers this; it never applies to the central place
  canRemove(establishment: any): boolean {
    return this.canAct() && !!this.api.removeFromEstablishment && establishment.establishmentId != null;
  }

  // drop every role of this person at the establishment (soft delete)
  removeFromEstablishment(establishment: any): void {
    if (!this.canRemove(establishment)) return;
    this.isLoading = true;
    this.api.removeFromEstablishment!(this.data.sumId, establishment.establishmentId).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Removed from the establishment.');
        this.markChanged();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to remove from the establishment.');
      },
    });
  }

  private markChanged(): void {
    this.changed = true;
    this.load();
  }

  close(): void {
    this.dialogRef.close(this.changed);
  }
}
